import { DataProjectModel } from "interfaces/models/dataProjectModel";
import {
  axiosCacheController,
  resetAxiosCacheController,
} from "services/baseAxios";
import { getBimFileInfo } from "utils/bim";
import { checkProjectGenerated } from "utils/cache";
import { cacheAbortController, renewCacheAbortController } from "utils/fetch";
import { logDev } from "utils/logs";
import { cacheModules } from "../caches";
import { CacheContext, CacheProjectStatus, errorCodes } from "../types";
import CacheService from "./CacheService";
import { message } from "components/base";
import { bimFileApi } from "apiClient/v2";

class CacheProject {
  private isRunning = false;
  private dataProjectModel!: DataProjectModel;

  public onDispatch!: () => void;

  constructor(public readonly project: CacheService) {
    this.pause = this.pause.bind(this);
  }

  protected async loadProjectDetail() {
    if (this.dataProjectModel) return;
    const { data: dataProjectModel } = await bimFileApi.getProject(
      this.project.id,
      false,
      true
    );
    this.dataProjectModel = dataProjectModel;
  }

  public dispatch() {
    this.onDispatch?.();
  }

  public async pause() {
    if (!this.project) return;

    // If project is not processing, do not pause
    if (!this.project.isRunning()) return;

    // If project is stopping, do not pause
    if (this.project.isStopping()) {
      message.success(
        `${this.dataProjectModel.name}のダウンロードが一時停止中です。少々お待ちください。`
      );

      return;
    }

    this.changeStatus(CacheProjectStatus.STOPPING);
    // this.cancelAlLRequest(); // Ignore for cache on multi projects
  }

  public async clear(): Promise<void> {
    if (this.project.isProcessing()) {
      await this.pause();
    }
    if (this?.project?.isClearing()) return;

    this.project.setStatus(CacheProjectStatus.IS_CLEARING);
    this.dispatch();

    try {
      await this.loadProjectDetail();
      await this.project.clear(this.dataProjectModel);
      this.dispatch();
    } catch (error) {
      this.project.setStatus(CacheProjectStatus.ERROR);
      this.dispatch();
      throw error;
    }
  }

  public getContext(): CacheContext {
    const urn = this.dataProjectModel.defaultBimPathId?.split("/").pop();
    const bimFileUrn = `${this.dataProjectModel.id}?version=${this.dataProjectModel.version}`;
    const versionId = encodeURIComponent(bimFileUrn);
    const { bimFileId, version } = getBimFileInfo(urn || "");

    return {
      project: this.dataProjectModel,
      versionId,
      bimFileId,
      version,
      cachedUrls: this.project.getKeysCached(),
    };
  }

  public async start(): Promise<void> {
    if (this.isRunning) {
      throw new Error(errorCodes.RUNNING);
    }

    this.isRunning = true;

    try {
      await this.loadProjectDetail();
      // this.generateNewAbortController(); // Ignore for cache on multi projects

      if (this.project.isProcessing()) throw new Error(errorCodes.PROCESSING);

      await this.runCache();

      // Stop caching and save project
      await this.stopCaching();
    } catch (error) {
      const isStopping = this.project.isStopping();

      // If project is not stopping, change status to error
      if (!this.project.isStopping()) {
        this.changeStatus(CacheProjectStatus.ERROR);
      } else if (this.project.isCacheDone()) {
        // If project is cache done, change status to finished
        this.changeStatus(CacheProjectStatus.FINISHED);
      } else {
        // If project is not cache done, change status to paused
        this.changeStatus(CacheProjectStatus.PAUSED);
      }

      // Stop caching and save project
      await this.stopCaching();

      if (!isStopping) throw error;
    } finally {
      this.isRunning = false;
    }
  }

  public async restart() {
    await this.loadProjectDetail();
    await this.clear();
    this.start();
  }

  protected async runCache() {
    if (this.project.isFinished()) {
      await this.beforeStartCaching();
    }

    this.changeStatus(CacheProjectStatus.PROCESSING);

    const { isGenerated } = await checkProjectGenerated(
      this.dataProjectModel,
      true
    );
    if (!isGenerated) {
      message.error(
        `${this.dataProjectModel.name}のデータが生成されていないため、データをダウンロードできません。`
      );
      throw new Error(errorCodes.NOT_GENERATED);
    }

    logDev(
      `Total caches: ${
        cacheModules.length
      } - ${this.project.getProgress()}% - ${this.project.getStatus()}`
    );
    for (const cache of cacheModules) {
      if (this.project.isStopping()) break;
      if (this.project.isCached(cache.key)) continue;
      await cache.handler(this.getContext(), this.project);
      this.project.setKeyCached(cache.key);
      await this.project.save();
      this.dispatch();
    }

    logDev("stop caching");
    // Change status and emit event before stop caching
    await this.beforeStopCaching();
  }

  /**
   * Before stop caching, we need to change status and emit event
   */
  protected async beforeStopCaching() {
    if (this.project.getProgress() === 100) {
      this.changeStatus(CacheProjectStatus.FINISHED);
      await this.project.updateResourceCached();
    } else {
      this.changeStatus(CacheProjectStatus.PAUSED);
    }
  }

  /**
   * Start caching, we need to change status and renew abort controller
   */
  protected async beforeStartCaching() {
    await this.project.loadNewestDataFromServer();
  }

  protected generateNewAbortController() {
    renewCacheAbortController();
    resetAxiosCacheController();
  }

  protected cancelAlLRequest() {
    axiosCacheController.abort("cancel cache");
    cacheAbortController.abort("cancel cache");
  }

  /**
   * Stop caching, we need to save project and dispatch event
   */
  protected async stopCaching() {
    await this.saveProject();
    if (this.project.getStatus() === CacheProjectStatus.FINISHED) {
      message.success(
        `${this.dataProjectModel.name}がダウンロードされました。`
      );
    }
  }

  protected changeStatus(status: CacheProjectStatus) {
    if (this.project.getStatus() === status) return;
    this.project.setStatus(status);
    this.dispatch();
  }

  protected async saveProject() {
    await this.project.save();
    this.dispatch();
  }
}

// eslint-disable-next-line import/no-anonymous-default-export
export default CacheProject;
